import React from "react";
import { Fragment, useContext, useEffect, useState } from "react";
import { Alert, Card ,OverlayTrigger,Tooltip} from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import { ProfileContext } from "../../Auth/ProfileProvider";
import Resident, { ApplicationStatus } from "../../Residents/Models/Resident";
import { deepCopy } from "../../Scripts/Common/DeepCopy";
import { abpnClient } from "../../Services/AbpnClient";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { TrainingProgram, TrainingProgramTypes } from "../../Shared/Models/TrainingProgram";
import { ClinicalSkillType } from "../Models/ClinicalSkill";
import { CertificationTrainingRequirement, EligibilityRequirements, KnownTrainingRequirementType, KnownCertificationType } from "../Models/EligibilityRequirements";
import { PostGraduateYear } from "../Models/PostGraduateYear";

interface IProps {
    resident: Resident
}

export function EligibilityRequirementsGrid(props: IProps) {
    const authContext = useContext(AuthContext);
    const { user } = useContext(ProfileContext);
    const [eligibilityRequirements, setEligibilityRequirements] = useState<Array<EligibilityRequirements>>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const hasExistingPsychAttestation = props.resident.trainingAttestations.find(_ => _.isAttestedComplete && _.certifications.some(_=>_.certificationId === KnownCertificationType.Psychiatry));
    const userProgramTypeId = user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId).trainingProgramTypeId;
    const isUserProgramTripleBoardOrPPP = userProgramTypeId === TrainingProgramTypes.TripleBoard || userProgramTypeId === TrainingProgramTypes.PostPediatricPortal;

    useEffect(() => {
        setIsLoading(true);

        const getCertificationRequirementResults = async () => {
            let url = `/api/training/GetEligibilityRequirements?physicianId=${props.resident.physicianId}&programTypeId=${userProgramTypeId}`
            let requirementResults = await abpnClient.get<Array<EligibilityRequirements>>(url, authContext);

            if(userProgramTypeId === TrainingProgramTypes.Cap) {
                if(props.resident.isOnFastTrack && await residentHasNecessaryCapPgys() && !hasExistingPsychAttestation){
                    setEligibilityRequirements(requirementResults)
                }
                else {
                    setEligibilityRequirements(requirementResults.filter(_ => getDisplayName(_) === "Child and Adolescent Psychiatry"));
                }
            }
            else {
                setEligibilityRequirements(requirementResults)
            }

            setIsLoading(false);
            return;
        }

        getCertificationRequirementResults();

        return () => setIsLoading(false);
    }, [props.resident]);

    const isChildNeuroAgeBasedCseRequirement = (cseRequirementType: number) => {
        if (cseRequirementType === ClinicalSkillType.CriticalCareChildNeurology || cseRequirementType === ClinicalSkillType.EpisodicChildNeurology ||
            cseRequirementType === ClinicalSkillType.NeurodegenerativeChildNeurology || cseRequirementType === ClinicalSkillType.NeuromuscularChildNeurology) return true;

            return false;
    }

    const getResidentChildNeuroAgeBasedCseSkill = (cseRequirementType: number) => {
        if (cseRequirementType === ClinicalSkillType.CriticalCareChildNeurology) {
            return props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === ClinicalSkillType.CriticalCareUnderTwoYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.CriticalCareTwoToTenYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.CriticalCareElevenToEighteenYearsOld );
        }

        if (cseRequirementType === ClinicalSkillType.EpisodicChildNeurology) {
            return props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === ClinicalSkillType.EpisodicUnderTwoYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.EpisodicTwoToTenYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.EpisodicElevenToEighteenYearsOld );
        }

        if (cseRequirementType === ClinicalSkillType.NeurodegenerativeChildNeurology) {
            return props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === ClinicalSkillType.NeurodegenerativeUnderTwoYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.NeurodegenerativeTwoToTenYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.NeurodegenerativeElevenToEighteenYearsOld );
        }

        if (cseRequirementType === ClinicalSkillType.NeuromuscularChildNeurology) {
            return props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === ClinicalSkillType.NeuromuscularUnderTwoYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.NeuromuscularTwoToTenYearsOld ||
                                                           x.clinicalSkillTypeId === ClinicalSkillType.NeuromuscularElevenToEighteenYearsOld );
        }
    }

    const getLegacyCseTypeId = (cseTypeId: number) => {
        if (cseTypeId == ClinicalSkillType.CriticalCareChildNeurology || cseTypeId== ClinicalSkillType.CriticalCareNeurology) return ClinicalSkillType.CriticalCare;

        if (cseTypeId == ClinicalSkillType.CriticalCareChildNeurology || cseTypeId == ClinicalSkillType.CriticalCareNeurology) return ClinicalSkillType.CriticalCare;

        if (cseTypeId == ClinicalSkillType.EpisodicChildNeurology || cseTypeId== ClinicalSkillType.EpisodicNeurology) return ClinicalSkillType.Episodic;

        if (cseTypeId == ClinicalSkillType.NeurodegenerativeChildNeurology || cseTypeId == ClinicalSkillType.NeurodegenerativeNeurology) return  ClinicalSkillType.Neurodegenerative;

        if (cseTypeId == ClinicalSkillType.NeuromuscularChildNeurology || cseTypeId == ClinicalSkillType.NeuromuscularNeurology) return ClinicalSkillType.Neuromuscular;

        return null;
    }

    const residentHasNecessaryCapPgys = async () => {
        let completedPgys = props.resident.postGraduateYears.filter(_ => _.isAttestedComplete);
        let completedPgyTrainingProgramIds = completedPgys.map(_ => _.trainingProgramId);

        let allTrainingPrograms = await getTrainingPrograms();
        let completedPgyTrainingPrograms = allTrainingPrograms.filter(_ => completedPgyTrainingProgramIds.includes(_.trainingProgramId));

        let psychTrainingPrograms = completedPgyTrainingPrograms.filter(_ => _.trainingProgramTypeId === TrainingProgramTypes.Psychiatry);
        let otherNonCapTrainingPrograms = completedPgyTrainingPrograms.filter(_ => (_.trainingProgramTypeId !== TrainingProgramTypes.Psychiatry) && (_.trainingProgramTypeId !== TrainingProgramTypes.Cap));

        let psychPgys = completedPgys.filter(_ => psychTrainingPrograms.find(program => program.trainingProgramId === _.trainingProgramId));
        let otherNonCapPgys = completedPgys.filter(_ => otherNonCapTrainingPrograms.find(program => program.trainingProgramId === _.trainingProgramId));
        let hasThreeCapAcceptedPgys = (psychPgys.length + otherNonCapPgys.length) >= 3;

        return (hasThreeCapAcceptedPgys && psychPgys.length >= 2);
    }

    const getTrainingPrograms = async () => {
        let programs = await abpnClient.get<Array<TrainingProgram>>("/api/trainingProgram/getTrainingPrograms", authContext);
        return programs;
    }

    const getDisplayName = (requirementModel: EligibilityRequirements) => {
        return requirementModel.certificationNames.join(", ");
    }

    const getClinicalSkillsToList = (requirementModel: EligibilityRequirements) => {
        let cseRequirements = requirementModel?.certificationTrainingRequirements?.filter(_ => _.requirementType === KnownTrainingRequirementType.RequiredForCseFinalAttestation && _.isRequired);
        if (!cseRequirements) return null;

        let requiredCSEs = cseRequirements.find(_ => _.subRequirementResults?.length)?.subRequirementResults;
        if (!requiredCSEs?.length) return null;

        return requiredCSEs.filter(requirement => requirement.isRequired).map(_ => {
            let legacyTypeId: number | null = getLegacyCseTypeId(_.clinicalSkillEvaluationType);
            let isChildNeuroAgeBasedRequirement: any = isChildNeuroAgeBasedCseRequirement(_.clinicalSkillEvaluationType);
            let requiredCseInProgressOrCompleted = isChildNeuroAgeBasedRequirement ? getResidentChildNeuroAgeBasedCseSkill(_.clinicalSkillEvaluationType) : props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === _.clinicalSkillEvaluationType);

            if (legacyTypeId && !requiredCseInProgressOrCompleted) requiredCseInProgressOrCompleted = props.resident.clinicalSkills.find(x => x.clinicalSkillTypeId === legacyTypeId);

            return (
                <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PsychClinicalSkillsInfo" bsPrefix="pc-tooltip-eligibility">{requiredCseInProgressOrCompleted == null ? "Not Entered" : requiredCseInProgressOrCompleted.isAttestedComplete  ? "Entered-Attested" : "Entered-Not Attested"}</Tooltip>}>
                <div className="ms-3">
                    <i className={requiredCseInProgressOrCompleted == null ? "la la-times-circle text-danger la-lg" :
                                  requiredCseInProgressOrCompleted.isAttestedComplete ? "la la-check-circle text-success la-lg" : "la la-exclamation-circle la-lg pc-warning"}>
                    </i>&nbsp;{requiredCseInProgressOrCompleted?.clinicalSkillTypeDescription ?? _.title}
                </div>
                 </OverlayTrigger>
            );
        })
    }

    const getCapCSE = () => {
        return getCapCSEs().find(_ => _.isAttestedComplete) ?? getCapCSEs()[0];
    }

    const getPgyList = (requirementModel: EligibilityRequirements) => {
        return Array(requirementModel.requiredNumberOfPGYs).fill('PGY');
    }

    const getPgyProgressIcon = (isPGYComplete: boolean | undefined = null) => {
        if (isPGYComplete === false) return <i className="la la-exclamation-circle la-lg pc-warning"></i>;

        if (isPGYComplete) return <i className="la la-check-circle text-success la-lg"></i>;

        return <i className="la la-times-circle text-danger la-lg"></i>;
    }

    const getPgyProgressTooltip = (isPGYComplete: boolean | undefined = null) => {
        if (isPGYComplete === false) return "Entered-Not Attested";

        if (isPGYComplete) return "Entered-Attested";

        return "Not Entered";
    }

    const getIsPgySectionComplete = (certificationTrainingRequirements: Array<CertificationTrainingRequirement>) => {
        let requirementViolations = certificationTrainingRequirements.find(_ => _.requirementType == KnownTrainingRequirementType.PostGraduateYear && _.isRequired && _.isComplete == false);
        let attetstationRequirementViolations = certificationTrainingRequirements.find(_ => _.requirementType == KnownTrainingRequirementType.RequiredForPgyFinalAttestation && _.isRequired && _.isComplete == false);

        return (!requirementViolations && !attetstationRequirementViolations);
    }

    const getTrainingYearsSectionProgressIcon = (requirementModel: EligibilityRequirements) => {
        if (getIsPgySectionComplete(requirementModel.certificationTrainingRequirements)) return <i className="la la-check-circle text-success la-lg"></i>

        return <i className="la la-exclamation-circle la-lg pc-warning"></i>;
    }

    const getTrainingYearsSectionProgressTooltip = (requirementModel: EligibilityRequirements) => {
        if (getIsPgySectionComplete(requirementModel.certificationTrainingRequirements)) return "Required PGYs entered and attested"

        return "Required PGY entry and/or attestation incomplete";
    }

    const getIsCseSectionComplete = (certificationTrainingRequirements: Array<CertificationTrainingRequirement>) => {
        let requirementViolations = certificationTrainingRequirements.find(_ => _.requirementType == KnownTrainingRequirementType.ClinicalSkillEvaluation && _.isRequired && _.isComplete == false);
        let attetstationRequirementViolations = certificationTrainingRequirements.find(_ => _.requirementType == KnownTrainingRequirementType.RequiredForCseFinalAttestation && _.isRequired && _.isComplete == false);

        return (!requirementViolations && !attetstationRequirementViolations);
    }

    const getCSEsSectionProgressIcon = (requirementModel: EligibilityRequirements) => {
        if (getIsCseSectionComplete(requirementModel.certificationTrainingRequirements)) return <i className="la la-check-circle text-success la-lg"></i>

        return <i className="la la-exclamation-circle la-lg pc-warning"></i>;
    }

    const getCSEsSectionProgressTooltip = (requirementModel: EligibilityRequirements) => {
        if (getIsCseSectionComplete(requirementModel.certificationTrainingRequirements)) return "Required CSEs entered and attested"

        return "Required CSE entry and/or attestation incomplete";
    }

    const getIsNextRequiredPgyComplete = (residentAcceptedPGys: Array<PostGraduateYear>) => {
        if (!residentAcceptedPGys?.length) return null;

        let requiredPgySequenceYear = residentAcceptedPGys[0].sequenceNumber;

        if (residentAcceptedPGys.filter(_ => _.sequenceNumber === requiredPgySequenceYear).find(_ => !_.isAttestedComplete)) return false;

        return true;
    }


    const getCapCSEs = () => {
        let cseList = deepCopy(props.resident.clinicalSkills);

        return cseList.filter(_ => _.clinicalSkillTypeId == ClinicalSkillType.CAP ||
            _.clinicalSkillTypeId == ClinicalSkillType.CapAdolescence ||
            _.clinicalSkillTypeId == ClinicalSkillType.CapPreSchool ||
            _.clinicalSkillTypeId == ClinicalSkillType.CapSchoolAged);
    }

    const getPsychClinicalSkillsToList = () => {
        let counter = 0;
        let existingPsychCSEs = deepCopy(props.resident.clinicalSkills.filter(_ => _.clinicalSkillTypeId === ClinicalSkillType.Psychiatry));
        let requiredPsychCSEs = ['Psychiatry', 'Psychiatry', 'Psychiatry']
        let capSupplementalCSE = getCapCSE();

        return (
            requiredPsychCSEs.map((_: string, index: number) => {
                counter++;
                let foundCSE = existingPsychCSEs.pop();

                if (!foundCSE && capSupplementalCSE && counter === 3) foundCSE = capSupplementalCSE

                return (
                    <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PsychClinicalSkillsInfo" bsPrefix="pc-tooltip-eligibility">{foundCSE == null ? "Not Entered" : foundCSE.isAttestedComplete ? "Entered-Attested" : "Entered-Not Attested"}</Tooltip>}>

                    <div className="ms-3" key={index}>
                        <i className={foundCSE == null ? "la la-times-circle text-danger la-lg" : foundCSE.isAttestedComplete ? "la la-check-circle text-success la-lg" : "la la-exclamation-circle la-lg pc-warning"}>
                        </i>&nbsp;{!foundCSE || foundCSE.clinicalSkillTypeId === ClinicalSkillType.Psychiatry ? `${_} ${counter}` : 'CAP'}
                    </div>
                    </OverlayTrigger>
                )
            })
        );
    }

    const getTripleBoardAndPppProgramPsychClinicalSkillsToList = () => {
        let counter = 0;
        let existingPsychCSEs = deepCopy(props.resident.clinicalSkills.filter(_ => _.clinicalSkillTypeId === ClinicalSkillType.Psychiatry));
        let requiredPsychCSEs = ['Psychiatry', 'Psychiatry']

        return (
            requiredPsychCSEs.map(_ => {
                counter++;
                let foundCSE = existingPsychCSEs.pop();

                return (
                    <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="TripleBoardAndPppInfo" bsPrefix="pc-tooltip-eligibility">{foundCSE == null ? "Not Entered" : foundCSE.isAttestedComplete ? "Entered-Attested" : "Entered-Not Attested"}</Tooltip>}>

                    <div className="ms-3">
                        <i className={foundCSE == null ? "la la-times-circle text-danger la-lg" : foundCSE.isAttestedComplete ? "la la-check-circle text-success la-lg" : "la la-exclamation-circle la-lg pc-warning"}>
                        </i>&nbsp;{!foundCSE || foundCSE.clinicalSkillTypeId === ClinicalSkillType.Psychiatry ? `${_} ${counter}` : 'CAP'}
                    </div>
                    </OverlayTrigger>
                )
            })
        );
    }

    const getCapClinicalSkillsToList = () => {
        let counter = 0;
        let requiredPsychCSEs = ['CAP', 'CAP', 'CAP']
        let capCSEs = getCapCSEs();

        return (
            requiredPsychCSEs.map((_: string, index: number) => {
                counter++;
                let foundCSE = capCSEs.pop();

                return (
                    <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="CapClinicalSkillsInfo" bsPrefix="pc-tooltip-eligibility">{foundCSE == null ? "Not Entered" : foundCSE.isAttestedComplete ? "Entered-Attested" : "Entered-Not Attested"}</Tooltip>}>

                    <div className="ms-3" key={index}>
                        <i className={foundCSE == null ? "la la-times-circle text-danger la-lg" : foundCSE.isAttestedComplete ? "la la-check-circle text-success la-lg" : "la la-exclamation-circle la-lg pc-warning"}>
                        </i>&nbsp;{foundCSE ? foundCSE.clinicalSkillTypeDescription : `${_} ${counter}`}
                    </div>
                    </OverlayTrigger>
                )
            })
        );
    }

    const getApplicationStatus = (applicationStatus: ApplicationStatus) => {
        switch (applicationStatus) {
            case ApplicationStatus.Ineligable:
                return "Not Eligible To Apply";
            case ApplicationStatus.Eligable:
                return "Eligible To Apply";
            case ApplicationStatus.Applied:
                return "Applied";

            default:
                throw new Error('Enumeration value out of range.');
        }
    }

    if (isLoading) return <AbpnSpinner />;

    return (
        <Fragment>
            {eligibilityRequirements?.map((requirementModel: EligibilityRequirements, modelIndex: number) => {
                let hasRequirementViolations = requirementModel.certificationTrainingRequirements.find(_ => (_.isComplete === false) && _.isRequired &&
                                                                                                       (_.requirementType === KnownTrainingRequirementType.PostGraduateYear ||
                                                                                                        _.requirementType === KnownTrainingRequirementType.ClinicalSkillEvaluation));

                let hasPgyRequirementViolations = requirementModel.certificationTrainingRequirements.find(_ => (_.isComplete === false) && _.isRequired && _.requirementType === KnownTrainingRequirementType.PostGraduateYear );
                let residentPGYs = [...requirementModel.residentAcceptedPGYs];
                let psychEligibilityRequirementModel = requirementModel.certificationIds.find(_ => _ === KnownCertificationType.Psychiatry);
                let applicationStatus = (psychEligibilityRequirementModel && props.resident.additionalFastTrackApplicationStatus) ? props.resident.additionalFastTrackApplicationStatus : props.resident.applicationStatus;

                return (
                         <Card key={modelIndex} className="mb-4 border-pc-secondary-grey">
                            <Card.Body>
                                <h5 className="fw-bolder mt-1 mb-1 text-primary">ABPN Eligibility Requirements</h5>
                                <h6 className="fw-bolder mt-2 mb-2 text-primary">{getDisplayName(requirementModel)}</h6>

                                <Card.Header className={`fw-bolder text-light mb-4 ${applicationStatus === ApplicationStatus.Ineligable ? 'bg-danger' : 'bg-success'}`}>
                                    <React.Fragment>
                                        {getApplicationStatus(applicationStatus)}
                                        {applicationStatus == ApplicationStatus.Ineligable &&
                                            <React.Fragment>
                                                &nbsp;<OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PgyInfo" bsPrefix="pc-tooltip-eligibility">Your residents will show yes once the application periods are open and you have all of the required information entered into the system.</Tooltip>}>
                                                <span><i className="las la-info-circle ml-1 text-pp-secondary"></i></span>
                                            </OverlayTrigger>
                                            </React.Fragment>
                                        }

                                    </React.Fragment>

                                </Card.Header>

                                {hasRequirementViolations &&
                                    <Alert variant="danger" className="pc-font-size-smaller">
                                        Requirement violations must be corrected before final attestation of completed residency training can be made.
                                    </Alert>
                                }

                            <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PgyInfo" bsPrefix="pc-tooltip-eligibility">{getTrainingYearsSectionProgressTooltip(requirementModel)}</Tooltip>}>
                            <p className="fw-bolder ms-2 mt-3 text-secondary">{getTrainingYearsSectionProgressIcon(requirementModel)} &nbsp;Post Graduate Years</p>
                            </OverlayTrigger>
                                {getPgyList(requirementModel).map((_: string, pgyIndex: number) => {
                                    let isNextRequiredPgyAttestedComplete = getIsNextRequiredPgyComplete(residentPGYs);
                                    let currentPgySequenceYear = residentPGYs[0]?.sequenceNumber;

                                    if (residentPGYs?.length) {
                                        let updatedPGYs = residentPGYs.filter(_ => _.sequenceNumber !== currentPgySequenceYear);
                                        residentPGYs = updatedPGYs;
                                    }

                                    return (
                                            <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PgyProgressInfo" bsPrefix="pc-tooltip-eligibility">{getPgyProgressTooltip(isNextRequiredPgyAttestedComplete)}</Tooltip>}>
                                                <div className="ms-3" key={pgyIndex + modelIndex}>
                                                    {getPgyProgressIcon(isNextRequiredPgyAttestedComplete)}&nbsp; {`${_} ${currentPgySequenceYear ?? ''}`}
                                                </div>
                                            </OverlayTrigger>
                                            );
                                })}

                                {hasPgyRequirementViolations &&
                                    <Alert variant="danger" className="pc-font-size-smaller mt-2">
                                        <div>PGY - Requirement Violations</div>
                                        {requirementModel.certificationTrainingRequirements.filter(_ => _.requirementType === KnownTrainingRequirementType.PostGraduateYear && (_.isComplete === false) && _.isRequired)
                                        .map((requirement: CertificationTrainingRequirement, requirementIndex: number) => {
                                            return (
                                                <Fragment key={requirementIndex + modelIndex + 1}>
                                                    <li className="ms-2 mt-1">{requirement.title}</li>
                                                </Fragment>
                                            );
                                        })}
                                    </Alert>
                                }

                                <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="CSEInfo" bsPrefix="pc-tooltip-eligibility">{getCSEsSectionProgressTooltip(requirementModel)}</Tooltip>}>
                                <p className="fw-bolder ms-2 mt-3 text-secondary">{getCSEsSectionProgressIcon(requirementModel)} &nbsp;Clinical Skills Evaluations</p>
                                </OverlayTrigger>

                                {requirementModel.certificationIds.find(_ => _ === KnownCertificationType.Psychiatry) ?
                                    (isUserProgramTripleBoardOrPPP ? getTripleBoardAndPppProgramPsychClinicalSkillsToList() : getPsychClinicalSkillsToList()) : null
                                }

                                {requirementModel.certificationIds.find(_ => _ === KnownCertificationType.CAP) && getCapClinicalSkillsToList()}

                                {getClinicalSkillsToList(requirementModel)}

                                {requirementModel.certificationTrainingRequirements.find(_ => (_.isComplete === false) && _.isRequired && _.requirementType === KnownTrainingRequirementType.ClinicalSkillEvaluation) &&
                                    <Alert variant="danger" className="pc-font-size-smaller mt-2">
                                        <div>CSE - Requirement Violations</div>
                                        {requirementModel.certificationTrainingRequirements.filter(_ => _.requirementType === KnownTrainingRequirementType.ClinicalSkillEvaluation && _.isComplete == false && _.isRequired)
                                        .map((requirement: CertificationTrainingRequirement, requirementIndex: number) => {
                                            return (
                                                <Fragment key={requirementIndex + modelIndex}>
                                                    <li className="ms-2 mt-1">{requirement.title}</li>
                                                </Fragment>
                                            );
                                        })}
                                    </Alert>
                                }
                                <hr />

                                <h6 className="fw-bolder ml-2 mt-1 mb-2 text-secondary">Training Status</h6>
                                <p className="ms-2 fw-bolder">
                                    {(requirementModel.isTrainingEligibleForFinalAttestation) ? <i className="la la-check-circle text-success la-lg"></i> : <i className="la la-exclamation-circle la-lg pc-warning"></i>}
                                    &nbsp;{(requirementModel.isTrainingEligibleForFinalAttestation) ? 'Completed (ready for PD attestation)' : 'Not Completed'}
                                </p>
                            </Card.Body>
                        </Card>
                );
            })}
        </Fragment>
    );
}
